import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Menu,
  MenuItem,
  Tab,
  Tabs,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import PopupState, {bindMenu, bindTrigger} from 'material-ui-popup-state';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {usePrint} from '../../../hooks/print';
import {useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import usePrevious from '../../../hooks/usePrevious';
import useShiftSelect, {isShiftClick} from '../../../hooks/useShiftSelect';
import {
  PRINT_HIDDEN_SX,
  PRINTABLE_CONTAINER_SX,
  PRINTABLE_MAP_SX,
} from '../../../utils/printStyles';
import {AdaptiveButtonGroup} from '../../common/AdaptiveButtonGroup';
// import {
//   OpenedEntityMode,
//   OpenedEntityType,
// } from '../../../utils/connect-view-panel';
//import AccessControl from '../../common/AccessControl';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {Map, /*MapLatLangCoordinates,*/ MapLayerId} from '../../common/Map';
import {ResizableColumns} from '../../common/ResizableColumns';
import TabLabel from '../../common/TabLabel';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
//import {ZoneSelect} from '../../selectors/ZoneSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import BeltLocationHistoryReportExportButton from './BeltLocationHistoryReportExportButton';

interface Props {
  value: BeltLocationHistoryReportData;
  onUpdate?: (value?: BeltLocationHistoryReportData) => void;
}

const DEFAULT_SHOWN_FIELDS = ['pos_lat', 'pos_lon', 'date'];

export interface BeltLocationHistoryReportDataTab {
  id: number;
  refreshInterval?: number | null;
  selectedIds?: string[];
  selectAll?: boolean;
  params?: {
    date_start?: string;
    date_end?: string;
    page?: number;
    limit?: number;
    order?: string;
    dir?: 'ASC' | 'DESC';
    status?: string;
  };
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  shownFields?: string[];
}

export interface BeltLocationHistoryReportData {
  activeId?: number | undefined;
  mapLayers?: MapLayerId[] | undefined;
  mapLevel: number | null;
  openedItems: BeltLocationHistoryReportDataTab[] | [];
}

export const getBeltLocationHistoryReportData =
  (): BeltLocationHistoryReportData => ({
    activeId: undefined,
    openedItems: [],
    mapLayers: ['mine', 'belt_location_history'],
    mapLevel: null,
  });

export const getBeltLocationHistoryReportDataTab = (
  id: number
): BeltLocationHistoryReportDataTab => ({
  id,
  selectAll: true,
  params: {
    date_start: dayjs().format('YYYY-MM-DD'),
    date_end: dayjs().format('YYYY-MM-DD'),
  },
  columnWidths: {},
  columnOrder: undefined,
  shownFields: DEFAULT_SHOWN_FIELDS,
});

export const BeltLocationHistory = ({value, onUpdate}: Props) => {
  const belts = useAppSelector(({assets}) => assets.belt_nodes);

  const isOpenAwayFromConnectView =
    location.pathname.includes('/panels/') ||
    !document.getElementById('connect-view-panel');

  const config: BeltLocationHistoryReportData = useMemo(() => {
    const v = value ?? getBeltLocationHistoryReportData();
    return {
      ...v,
      mapLayers: v.mapLayers ?? ['belt_location_history', 'street', 'mine'],
      mapLevel: v.mapLevel ?? null,
    };
  }, [value]);

  const openedItemIndex = useMemo(
    () =>
      (config.activeId
        ? config.openedItems.findIndex((i) => i.id === config.activeId)
        : null) ?? config.openedItems.length - 1,
    [config.activeId, config.openedItems]
  );

  const openedItem = useMemo(
    () =>
      openedItemIndex !== -1
        ? config.openedItems[openedItemIndex]
          ? config.openedItems[openedItemIndex]
          : getBeltLocationHistoryReportDataTab(config.activeId as number)
        : null,
    [openedItemIndex, config.openedItems]
  );
  const openedCommtracId = useMemo(() => {
    const belt = belts.find((i: any) => i.id === openedItem?.id);
    return belt?.commtrac_external_id;
  }, [config.activeId, config.openedItems]);

  const tabNames = useMemo(
    () =>
      config.openedItems.map((o) => {
        const belt = belts.find((i: any) => i.id === o.id);
        return belt?.name || `Belt #${belt?.id}`;
      }),
    [config.openedItems, belts]
  );

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] = useState<any>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const params = useMemo(
    () =>
      openedItem?.id
        ? {
            date_start:
              openedItem.params?.date_start ?? dayjs().format('YYYY-MM-DD'),
            date_end:
              openedItem.params?.date_end ?? dayjs().format('YYYY-MM-DD'),
            page: openedItem.params?.page ?? 0,
            limit: openedItem.params?.limit ?? 25,
            order: openedItem.params?.order ?? 'date',
            dir: openedItem.params?.dir ?? 'DESC',
            status: openedItem.params?.status ?? 'all',
          }
        : null,
    [openedItem?.id, openedItem?.params]
  );

  const fetchData = useCallback(
    async (params: any) => {
      try {
        setFetchedInProgress(true);
        try {
          const resp = await API.get(
            `${apiBaseUrl}/belt/${openedCommtracId}/location`,
            {params}
          );
          setFetchedData(resp.data);
        } catch (error: any) {
          const messages = getMessagesFromApiError(error, 'STRA_5fe7343b');
          setFetchedErrors(messages);
        }
        setFetchedInProgress(false);
      } catch (error) {
        console.log(error);
      }
    },
    [params, openedItem]
  );

  useEffect(() => {
    if (params) {
      fetchData(params);
    }
  }, [params]);

  useEffect(() => {
    if (!openedItem) {
      setFetchedData(undefined);
    }
  }, [openedItem]);

  /****************/
  /* auto refresh */
  /****************/

  const callFetchData = useCallback(() => {
    params && fetchData(params);
  }, [params]);

  useRefreshInterval(callFetchData, openedItem?.refreshInterval);

  /*********/
  /* grid  */
  /*********/
  const shownFields = useMemo(
    () => openedItem?.shownFields ?? DEFAULT_SHOWN_FIELDS,
    [openedItem?.shownFields]
  );
  const dataGridRef = useRef<DataGridRef>(null);
  const printContainerRef = useRef<HTMLDivElement>(null);
  const [printReport] = usePrint(printContainerRef.current);
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<any>[] = [
    {
      field: 'select',
      type: 'select',
      width: 60,
      hideable: false,
      renderHeader: () => {
        return (
          <Checkbox
            color="primary"
            disabled={rows.length === 0}
            checked={selectedItems.length > 0 && selectedAll}
            indeterminate={selectedItems.length > 0 && !selectedAll}
            onChange={() => toggleSelectAllItems()}
          />
        );
      },
      renderCell: ({row, visibleRows}) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(`${row.id}`)}
          onChange={(event) =>
            toggleSelectItem(
              `${row.id}`,
              isShiftClick(event),
              visibleRows.map((visibleRow) => `${visibleRow.id}`)
            )
          }
        />
      ),
    },
    {
      field: 'date',
      headerName: 'Timestamp',
      width: 160,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.date,
      renderCell: ({row}) => row.date,
    },
    {
      field: 'pos_lon',
      headerName: 'Lon',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.pos_lon,
      renderCell: ({row}) => row.pos_lon,
    },
    {
      field: 'pos_lat',
      headerName: 'Lat',
      width: 120,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.pos_lat,
      renderCell: ({row}) => row.pos_lat,
    },
    {
      field: 'beacon_data',
      headerName: 'Beacon Data',
      width: 200,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.beacon_data,
      renderCell: ({row}) => row.beacon_data,
    },
    {
      field: 'error_ellipse_major_radius',
      headerName: 'Error Ellipse Major Radius',
      width: 200,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.error_ellipse_major_radius,
      renderCell: ({row}) => row.error_ellipse_major_radius,
    },
    {
      field: 'error_ellipse_minor_radius',
      headerName: 'Error Ellipse Minor Radius',
      width: 210,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.error_ellipse_minor_radius,
      renderCell: ({row}) => row.error_ellipse_minor_radius,
    },
    {
      field: 'error_ellipse_orientation',
      headerName: 'Error Ellipse Orientation',
      width: 220,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.error_ellipse_orientation,
      renderCell: ({row}) => row.error_ellipse_orientation,
    },
  ];

  const exportFields = useMemo(
    () =>
      columns
        .filter((col) => !!col.headerName)
        .map((col) => ({
          field: col.field,
          label: col.headerName,
          hidden: !shownFields?.includes(col.field),
        })),
    [shownFields]
  );

  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = useMemo(
    () =>
      openedItem?.selectAll
        ? rows.map((it: any) => it.id.toString())
        : openedItem?.selectedIds ?? [],
    [rows, openedItem]
  );

  const selectedRows = useMemo(
    () => rows.filter((i: any) => selectedItems?.includes(`${i.id}`)),
    [rows, selectedItems]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const getNextSelectedIds = useShiftSelect<string>();

  const toggleSelectItem = (
    id: string,
    shiftKey: boolean,
    visibleIds: string[]
  ) => {
    if (openedItem) {
      const nextSelectedIds = getNextSelectedIds({
        id,
        visibleIds,
        selectedIds: selectedItems ?? [],
        shiftKey,
      });
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              $set: {
                ...openedItem,
                selectedIds: nextSelectedIds,
                selectAll: nextSelectedIds.length === fetchedData?.items.length,
              },
            },
          },
        })
      );
    }
  };

  const selectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectAll: {
                $set: true,
              },
              selectedIds: {
                $set: rows?.map((i: any) => `${i.id}`) ?? [],
              },
            },
          },
        })
      );
    }
  };

  const unselectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectAll: {
                $set: false,
              },
              selectedIds: {
                $set: [],
              },
            },
          },
        })
      );
    }
  };

  const toggleSelectAllItems = () => {
    if (selectedItems.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  const prevSelectedAll = usePrevious(selectedAll);

  useEffect(() => {
    if ((prevSelectedAll && !selectedAll) || !openedItem?.selectedIds) {
      selectAll();
    }
  }, [rows]);

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              columnWidths: {$set: widths},
            },
          },
        })
      );
    }
  };

  const handleShownFieldsChange = useCallback(
    (fields: string[]) => {
      if (openedItem) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                shownFields: {$set: fields},
              },
            },
          })
        );
      }
    },
    [onUpdate, config, openedItemIndex, openedItem]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      if (openedItem) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                columnOrder: {$set: order},
                shownFields: {$set: fields},
              },
            },
          })
        );
      }
    },
    [onUpdate, config, openedItemIndex, openedItem]
  );

  return (
    <>
      <DashboardPanelTitleSlot>
        Belt Location History Report
      </DashboardPanelTitleSlot>

      <Box ref={printContainerRef} sx={PRINTABLE_CONTAINER_SX}>
        <ResizableColumns
          left={
            <Map
              sx={PRINTABLE_MAP_SX}
              panel="belt_location_tracking_report"
              selectedMapLayers={config.mapLayers}
              selectedLevel={config.mapLevel}
              availableMapLayers={['street', 'mine', 'belt_location_history']}
              beltLocationHistoryData={selectedRows}
              minWidth={250}
              onSelectMapLayers={(v) => {
                onUpdate?.(
                  update(config, {
                    mapLayers: {
                      $set: v,
                    },
                  })
                );
              }}
              onSelectLevel={(v) => {
                onUpdate?.(
                  update(config, {
                    mapLevel: {
                      $set: v,
                    },
                  })
                );
              }}
            />
          }
        >
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            width="100%"
            overflow="hidden"
          >
            <Box
              sx={PRINT_HIDDEN_SX}
              display="flex"
              flexDirection="column"
              gap={3}
              py={1.5}
              bgcolor={(theme) =>
                theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF'
              }
            >
              {isOpenAwayFromConnectView && (
                <Box pr={1.5}>
                  <PopupState variant="popover" popupId="demo-popup-menu">
                    {(popupState) => (
                      <>
                        <Button variant="outlined" {...bindTrigger(popupState)}>
                          Select an Object
                        </Button>
                        <Menu {...bindMenu(popupState)}>
                          {belts?.map((it) => (
                            <MenuItem
                              key={it.id}
                              onClick={() => {
                                const updateObject: any = {
                                  activeId: {
                                    $set: it.id ?? undefined,
                                  },
                                };
                                // if item is not already in openedItems add it
                                if (
                                  !config.openedItems.find(
                                    (itm) => itm.id === it.id
                                  )
                                ) {
                                  updateObject.openedItems = {
                                    $set: [
                                      ...config.openedItems,
                                      {
                                        id: it.id ?? -1,
                                      },
                                    ],
                                  };
                                }
                                onUpdate?.(update(value, updateObject));
                                popupState.close();
                              }}
                            >
                              {it.name || `Belt #${it?.id}`}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                  </PopupState>
                </Box>
              )}
              {config.openedItems.length ? (
                <Box pr={2}>
                  <Tabs
                    value={openedItem?.id}
                    variant="scrollable"
                    onChange={(_event, v) => {
                      if (v) {
                        onUpdate?.(
                          update(config, {
                            activeId: {
                              $set: v,
                            },
                          })
                        );
                      }
                    }}
                  >
                    {config.openedItems.map((i, idx) => (
                      <Tab
                        key={i.id}
                        value={i.id}
                        label={
                          <TabLabel
                            name={tabNames?.[idx] ?? 'Undefined'}
                            onClose={() => {
                              onUpdate?.(
                                update(config, {
                                  activeId: {
                                    $set:
                                      config.activeId === i.id
                                        ? config.openedItems[0].id
                                        : config.activeId,
                                  },
                                  openedItems: {
                                    $set:
                                      config.openedItems.filter(
                                        (o) => o.id && o.id !== i.id
                                      ) ?? [],
                                  },
                                })
                              );
                            }}
                          />
                        }
                      />
                    ))}
                  </Tabs>
                </Box>
              ) : (
                <Box minWidth={400} pr={1.5}>
                  <Alert severity="warning">No Assets Opened</Alert>
                </Box>
              )}

              <Box display="flex" flexDirection="column" pr={1.5}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Box display="flex" gap={1}>
                    <Box minWidth={400}>
                      <DateRangeSelect
                        value={
                          openedItem
                            ? [
                                dayjs(openedItem.params?.date_start).toDate(),
                                dayjs(openedItem.params?.date_end).toDate(),
                              ]
                            : undefined
                        }
                        size="small"
                        disabled={!openedItem}
                        onChange={(v) => {
                          if (openedItem) {
                            onUpdate?.(
                              update(config, {
                                openedItems: {
                                  [openedItemIndex]: {
                                    $set: {
                                      ...openedItem,
                                      selectAll: true,
                                      params: {
                                        ...openedItem.params,
                                        date_start: v?.[0]
                                          ? dayjs(v?.[0]).format('YYYY-MM-DD')
                                          : undefined,
                                        date_end: v?.[0]
                                          ? dayjs(v?.[1]).format('YYYY-MM-DD')
                                          : undefined,
                                      },
                                    },
                                  },
                                },
                              })
                            );
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  <Box display="flex" sx={{flex: 1, minWidth: 0}}>
                    <Box sx={{flex: 1, minWidth: 0, textAlign: 'right'}}>
                      <AdaptiveButtonGroup disabled={!openedItem}>
                        <Button size="small" data-priority={1}>
                          <BeltLocationHistoryReportExportButton
                            value={openedItem}
                            commtrac_external_id={openedCommtracId}
                            params={params ?? undefined}
                            exportFields={exportFields}
                          />
                        </Button>

                        <Button
                          size="small"
                          onClick={() => params && fetchData(params)}
                          data-priority={3}
                        >
                          <RefreshIcon />
                        </Button>

                        <AutoRefreshSelect
                          value={openedItem?.refreshInterval ?? null}
                          data-priority={2}
                          onChange={(v) => {
                            if (openedItem) {
                              onUpdate?.(
                                update(config, {
                                  openedItems: {
                                    [openedItemIndex]: {
                                      $set: {
                                        ...openedItem,
                                        refreshInterval: v,
                                      },
                                    },
                                  },
                                })
                              );
                            }
                          }}
                        />

                        <Button
                          size="small"
                          onClick={() => printReport()}
                          data-priority={4}
                        >
                          <PrintIcon />
                        </Button>
                      </AdaptiveButtonGroup>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Backdrop
              open={fetchedInProgress}
              sx={{position: 'absolute', zIndex: 1199}}
            >
              <CircularProgress color="inherit" />
            </Backdrop>

            {fetchedErrors.map((error, idx) => (
              <Alert
                key={idx}
                severity="error"
                onClose={() => params && fetchData(params)}
              >
                {error}
              </Alert>
            ))}

            <DataGrid
              ref={dataGridRef}
              rows={rows}
              columns={columns}
              loading={fetchedInProgress}
              shownFields={shownFields}
              defaultShownFields={DEFAULT_SHOWN_FIELDS}
              columnWidths={openedItem?.columnWidths}
              columnOrder={openedItem?.columnOrder}
              pagination
              paginationMode="server"
              size="small"
              sortBy={
                params?.order
                  ? {
                      field: params?.order,
                      dir: params?.dir === 'DESC' ? 'desc' : 'asc',
                    }
                  : null
              }
              sortingMode="server"
              page={params?.page}
              pageSize={params?.limit}
              rowCount={fetchedData?.count}
              sxFooter={{
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
              }}
              onPageChange={(v) => {
                if (openedItem) {
                  onUpdate?.(
                    update(config, {
                      openedItems: {
                        [openedItemIndex]: {
                          $set: {
                            ...openedItem,
                            selectAll: true,
                            params: {
                              ...openedItem.params,
                              page: v,
                            },
                          },
                        },
                      },
                    })
                  );
                }
              }}
              onPageSizeChange={(v) => {
                if (openedItem) {
                  onUpdate?.(
                    update(config, {
                      openedItems: {
                        [openedItemIndex]: {
                          $set: {
                            ...openedItem,
                            selectAll: true,
                            params: {
                              ...openedItem.params,
                              page: 0,
                              limit: v,
                            },
                          },
                        },
                      },
                    })
                  );
                }
              }}
              onSort={(v) => {
                if (v && openedItem) {
                  onUpdate?.(
                    update(config, {
                      openedItems: {
                        [openedItemIndex]: {
                          $set: {
                            ...openedItem,
                            params: {
                              ...openedItem.params,
                              order: v.field,
                              dir: v.dir === 'desc' ? 'DESC' : 'ASC',
                            },
                          },
                        },
                      },
                    })
                  );
                }
              }}
              onShownFieldsChange={handleShownFieldsChange}
              onColumnWidthsChange={handleColumnWidthChange}
              onColumnOrderAndShownFieldsChange={
                handleColumnOrderAndShownFieldsChange
              }
            />
          </Box>
        </ResizableColumns>
      </Box>
    </>
  );
};
